/**
 * Formatters - Fonctions de formatage réutilisables
 *
 * Centralise le formatage de dates, nombres, durées, unités
 * pour garantir cohérence affichage dans toute l'app
 */
import i18n from '../i18n';

/**
 * Indique si la langue courante est l'anglais (singleton i18n, util non-React —
 * même pattern que `equineAge` / `enumLabel`). Dette [i18n-web-date-locale] lot 1.
 */
const isEnglish = () => String(i18n.language || 'fr').toLowerCase().startsWith('en');

/**
 * Formate une date selon la langue courante.
 *
 * - FR (défaut) : numérique « JJ/MM/AAAA » (+ « HH:MM » si includeTime) —
 *   STRICTEMENT INCHANGÉ vs avant (concat locale-naïve, fuseau local).
 * - EN : en-GB mois en lettres « 15 May 2026 » (+ « at 14:08 » si includeTime,
 *   connecteur natif en-GB via Intl).
 *
 * Fuseau : local dans les deux cas (getDate()/Intl sans timeZone) — comportement
 * d'origine préservé. Signature et valeurs de repli (`N/A`/`Date invalide`)
 * inchangées : les 15 appelants ne sont pas touchés.
 *
 * @param {string|Date} date - Date à formater
 * @param {boolean} includeTime - Inclure l'heure (défaut: true)
 * @returns {string} Date formatée selon la langue
 */
export const formatDate = (date, includeTime = true) => {
  if (!date) return 'N/A';

  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Date invalide';

  if (isEnglish()) {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(d);
  }

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  if (!includeTime) {
    return `${day}/${month}/${year}`;
  }

  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Formate une date au format court selon la langue courante.
 *
 * - FR (défaut) : numérique « JJ/MM » (sans année) — INCHANGÉ vs avant.
 * - EN : en-GB jour + mois abrégé « 15 Aug » (sans année, miroir du FR).
 *
 * @param {string|Date} date - Date à formater
 * @returns {string} Date courte selon la langue
 */
export const formatDateShort = (date) => {
  if (!date) return 'N/A';

  const d = new Date(date);
  if (isNaN(d.getTime())) return 'N/A';

  if (isEnglish()) {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(d);
  }

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');

  return `${day}/${month}`;
};

/**
 * Formate une heure selon la langue courante (helper HEURE, dette [i18n-web-date-locale]
 * lot owner O1). Miroir asymétrique de `formatDate` via `isEnglish()` :
 *
 * - FR (défaut) : « 10h31 » (séparateur 'h') — BYTE-FOR-BYTE l'existant, qu'il vienne
 *   d'un `toLocaleTimeString(...).replace(':', 'h')` (entrée Date) ou d'un champ string
 *   « HH:MM » déjà stocké sur lequel on faisait `.replace(':', 'h')`.
 * - EN : « 10:31 » (deux-points, en-GB natif).
 *
 * Accepte une Date/timestamp OU une string « HH:MM[:SS] » déjà formatée. Pour l'entrée
 * string, le swap reste minimal (premier ':' seulement) pour rester strictement byte-for-byte
 * vs l'ancien `.replace(':', 'h')` (aucune normalisation du padding).
 *
 * Option `hourOnly` (marqueur heure-seule, dette [i18n-owner-hourlabel-heure-seule] lot O3) :
 * pour une Date, rend l'HEURE SEULE — FR « 08h » (byte-for-byte l'ancien `HH+'h'` de
 * hourlyCompleteness) / EN « 08:00 » (deux-points, cohérent avec les axes/créneaux colon).
 * N'affecte pas l'entrée string (aucun appelant heure-seule ne passe une string).
 *
 * @param {string|Date|number} input - Heure à formater (Date, timestamp, ou « HH:MM »)
 * @param {{hourOnly?: boolean}} [options] - hourOnly: rendu heure-seule (Date uniquement)
 * @returns {string} Heure formatée selon la langue
 */
export const formatTime = (input, { hourOnly = false } = {}) => {
  if (input === null || input === undefined || input === '') return 'N/A';

  // Entrée déjà au format « HH:MM » (champ stocké) : swap minimal, byte-for-byte.
  if (typeof input === 'string' && /^\d{1,2}:\d{2}/.test(input)) {
    return isEnglish() ? input : input.replace(':', 'h');
  }

  const d = new Date(input);
  if (isNaN(d.getTime())) return 'N/A';

  const hours = String(d.getHours()).padStart(2, '0');

  if (hourOnly) {
    return isEnglish() ? `${hours}:00` : `${hours}h`;
  }

  const minutes = String(d.getMinutes()).padStart(2, '0');

  return isEnglish() ? `${hours}:${minutes}` : `${hours}h${minutes}`;
};

/**
 * Passe-plat `Intl.DateTimeFormat` où seule la LOCALE bascule (fr-FR ↔ en-GB via
 * `isEnglish()`), les OPTIONS d'appel étant préservées telles quelles (dette
 * [i18n-web-date-locale] lot owner O1).
 *
 * Distinct de `formatDate` (qui impose un FR numérique via concat manuelle) : ce helper
 * respecte le format riche existant de chaque site — mois en lettres, secondes, etc. —
 * et le rend simplement byte-for-byte en FR tout en le localisant en EN. À utiliser pour
 * migrer un `toLocaleDateString('fr-FR', opts)` / `toLocaleString('fr-FR', opts)` en lui
 * passant SES options actuelles.
 *
 * @param {string|Date|number} date - Date à formater
 * @param {Intl.DateTimeFormatOptions} options - Options passées à Intl (préservées)
 * @returns {string} Date formatée selon la langue
 */
export const formatDateLocale = (date, options) => {
  if (!date) return 'N/A';

  const d = new Date(date);
  if (isNaN(d.getTime())) return 'N/A';

  const locale = isEnglish() ? 'en-GB' : 'fr-FR';
  return new Intl.DateTimeFormat(locale, options).format(d);
};

/**
 * Composants de date selon la langue courante ({ day, month, weekday, hour, … }),
 * pour les motifs imposés que Intl ne produit pas tel quel (ex. « lun. 06/10 14h »).
 * Même locale que formatDateLocale. Retourne null si la date est invalide.
 */
export const formatDatePartsLocale = (date, options) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const locale = isEnglish() ? 'en-GB' : 'fr-FR';
  return Object.fromEntries(
    new Intl.DateTimeFormat(locale, options).formatToParts(d)
      .filter(p => p.type !== 'literal')
      .map(p => [p.type, p.value])
  );
};

/**
 * Reformate un titre de session AUTO-GÉNÉRÉ selon la langue, à l'AFFICHAGE uniquement
 * (dette [i18n-web-date-locale] lot owner O1). La DB n'est jamais modifiée.
 *
 * Le back-end génère `Session JJ/MM/AAAA` (sessionUploadController : date fr-FR figée).
 * En EN, on réaffiche « Session 27 May 2026 ». Tout titre qui ne matche PAS strictement
 * le pattern auto-généré (renommé par l'utilisateur, « Session Mobile <cheval> », etc.)
 * est retourné INTACT. En FR, le titre est toujours retourné tel quel (byte-for-byte).
 *
 * @param {string} title - Titre stocké de la session
 * @returns {string} Titre à afficher
 */
export const formatSessionTitle = (title) => {
  if (!title || !isEnglish()) return title;

  const m = title.match(/^Session (\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return title;

  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (isNaN(d.getTime())) return title;

  return `Session ${formatDateLocale(d, { day: 'numeric', month: 'long', year: 'numeric' })}`;
};

/**
 * Locale des nombres selon la langue de l'interface, même règle que les dates
 * (fr-FR / en-GB). Sert aussi d'option `locale` aux graphiques Chart.js.
 */
export const numberLocale = () => (isEnglish() ? 'en-GB' : 'fr-FR');

/**
 * Nombre formaté selon la langue : « 1 234,5 » en FR, « 1,234.5 » en EN.
 * Le regroupement FR d'Intl est une espace fine insécable (U+202F), absente des
 * polices intégrées de jsPDF : remplacée par l'espace insécable (U+00A0), qui
 * s'affiche à l'écran comme dans les PDF.
 *
 * @param {number} value
 * @param {number} [decimals=1] décimales fixes
 * @returns {string|null} null si la valeur n'est pas un nombre fini (l'appelant
 *   choisit sa mention : « Non mesuré », « Non renseigné »)
 */
export const formatNumberLocale = (value, decimals = 1) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat(numberLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value).replace(/\u202F/g, '\u00A0');
};

/**
 * Formate un nombre avec séparateurs de milliers, selon la langue
 * @param {number} num - Nombre à formater
 * @param {number} decimals - Nombre de décimales (défaut: 0)
 * @returns {string} "1 234,56" (FR) / "1,234.56" (EN)
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) return 'N/A';
  return formatNumberLocale(Number(num), decimals);
};

/**
 * Formate une valeur PM avec unité
 * @param {number} value - Valeur PM en μg/m³
 * @param {number} decimals - Nombre de décimales (défaut: 1)
 * @returns {string} "12,5 μg/m³"
 */
export const formatPM = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${formatNumber(value, decimals)} μg/m³`;
};

/**
 * Formate une température avec unité
 * @param {number} value - Température en °C
 * @param {number} decimals - Nombre de décimales (défaut: 1)
 * @returns {string} "23,5 °C"
 */
export const formatTemperature = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${formatNumber(value, decimals)} °C`;
};

/**
 * Formate une humidité avec unité
 * @param {number} value - Humidité en %
 * @param {number} decimals - Nombre de décimales (défaut: 0)
 * @returns {string} "65 %"
 */
export const formatHumidity = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${formatNumber(value, decimals)} %`;
};

